/**
 * Referral-request Tasks for practice-wide / system-wide admin views.
 */

import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import type { Practitioner, Task } from '@medplum/fhirtypes';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUrlFilterState } from '@/hooks/useUrlFilterState';
import { usePermissions } from '../../contexts/PermissionContext';
import { CANCELLATION_QUEUED_EXT } from '@unify/fhir-model/constants';
import {
  formatPractitionerPreferredLabel,
  formatPractitionerStaffLabel,
  getPractitionerOrganizationRef,
  getRole,
} from '@unify/fhir-model/resources/practitioner';
import {
  buildReferralRequestTaskCode,
  buildReferralRequestTaskInputs,
  computeReferralRequestMetrics,
} from '../../fhir-model/resources/referral-task';
import {
  type ReferralUiStatusLabel,
  searchReferralRequestTasks,
} from '../../lib/referralRequestsFhir';
import { executeTaskMutation } from '../../lib/taskLifecycleBots';
import { getDefaultScope, type ResourceScope } from '../../lib/rbac/scopeAccess';
import {
  type ReferralTaskRow,
  taskPreferencesFromRow,
  taskToRow,
} from '../caseload/tabs/useReferralTasks';

export type { ReferralTaskRow };
export { taskPreferencesFromRow };

export interface ProviderOption {
  value: string;
  label: string;
}

const ALL_SCOPE_PROVIDER_ROLES = new Set(['provider', 'supervisor']);
const ALL_STATUS_LABELS: ReferralUiStatusLabel[] = ['Pending', 'In Progress', 'Completed', 'Cancelled'];

function practitionerDisplayName(p: Practitioner): string {
  return formatPractitionerStaffLabel(p) || 'Provider';
}

function practitionerOrganizationRef(p: Practitioner): string | undefined {
  return getPractitionerOrganizationRef(p)?.reference;
}

function referralOwnerAndMeta(ownerOrgRef: string | undefined): Pick<Task, 'owner' | 'meta'> {
  if (!ownerOrgRef) return {};
  return {
    owner: { reference: ownerOrgRef },
    meta: { accounts: [{ reference: ownerOrgRef }] },
  };
}

export function useAdminReferralTasks(): {
  rows: ReferralTaskRow[];
  loading: boolean;
  error: string | null;
  scope: ResourceScope;
  showPracticeColumn: boolean;
  providerOptions: ProviderOption[];
  providers: Practitioner[];
  statusFilter: ReferralUiStatusLabel[];
  setStatusFilter: (statuses: ReferralUiStatusLabel[]) => void;
  refetch: () => Promise<void>;
  createReferralRequest: (input: {
    requesterPractitionerId: string;
    numberOfClients: number;
    acceptsInPerson: boolean;
    noMinors: boolean;
    noCouplesFamilies: boolean;
    onlyCashPay: boolean;
  }) => Promise<void>;
  cancelReferralRequest: (task: Task) => Promise<void>;
} {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const { role, ready: permissionsReady } = usePermissions();
  const scope = getDefaultScope(role);
  const showPracticeColumn = scope === 'ALL';

  const practiceOrganizationRef =
    profile?.resourceType === 'Practitioner'
      ? getPractitionerOrganizationRef(profile as Practitioner)?.reference
      : undefined;

  const [tasks, setTasks] = useState<Task[]>([]);
  const [providers, setProviders] = useState<Practitioner[]>([]);
  const [statusFilter, setStatusFilter] = useUrlFilterState('status', ALL_STATUS_LABELS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadProviders = useCallback(async (): Promise<Practitioner[]> => {
    const all = await medplum.searchResources('Practitioner', {
      active: 'true',
      _count: '200',
    });
    if (scope === 'ALL') {
      return (all as Practitioner[]).filter((p) => {
        const r = getRole(p);
        return r != null && ALL_SCOPE_PROVIDER_ROLES.has(r);
      });
    }
    if (!practiceOrganizationRef) {
      return [];
    }
    return (all as Practitioner[]).filter(
      (p) => practitionerOrganizationRef(p) === practiceOrganizationRef,
    );
  }, [medplum, scope, practiceOrganizationRef]);

  const inFlightRef = useRef(false);
  const loadGenRef = useRef(0);

  const load = useCallback(async () => {
    if (!permissionsReady) {
      return;
    }
    if (scope === 'PRACTICE' && !practiceOrganizationRef) {
      setTasks([]);
      setProviders([]);
      setError('No practice organization is linked to your profile.');
      setLoading(false);
      return;
    }

    const gen = ++loadGenRef.current;
    inFlightRef.current = true;
    setLoading(true);
    setError(null);
    try {
      medplum.invalidateSearches('Task');
      const [taskList, providerList] = await Promise.all([
        searchReferralRequestTasks(medplum, {
          scope,
          practiceOrganizationRef,
          statuses: statusFilter as ReferralUiStatusLabel[],
        }),
        loadProviders(),
      ]);
      if (gen !== loadGenRef.current) return;
      setTasks(taskList);
      setProviders(providerList);
    } catch (e) {
      if (gen !== loadGenRef.current) return;
      setError(e instanceof Error ? e.message : 'Failed to load referral requests');
      setTasks([]);
      setProviders([]);
    } finally {
      if (gen === loadGenRef.current) {
        inFlightRef.current = false;
        setLoading(false);
      }
    }
  }, [medplum, permissionsReady, scope, practiceOrganizationRef, statusFilter, loadProviders]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'visible' && !inFlightRef.current) {
        void load();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [load]);

  const rows = useMemo(() => {
    const out: ReferralTaskRow[] = [];
    for (const t of tasks) {
      const row = taskToRow(t, 'Provider');
      if (row) out.push(row);
    }
    return out;
  }, [tasks]);

  const providerOptions = useMemo(
    (): ProviderOption[] =>
      providers
        .filter((p) => p.id)
        .map((p) => ({
          value: p.id as string,
          label: practitionerDisplayName(p),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [providers],
  );

  const createReferralRequest = useCallback(
    async (input: {
      requesterPractitionerId: string;
      numberOfClients: number;
      acceptsInPerson: boolean;
      noMinors: boolean;
      noCouplesFamilies: boolean;
      onlyCashPay: boolean;
    }) => {
      const requesterId = input.requesterPractitionerId.trim();
      if (!requesterId) throw new Error('Select a provider for this referral request.');

      const requester =
        providers.find((p) => p.id === requesterId) ??
        (await medplum.readResource('Practitioner', requesterId));
      const requesterLabel = formatPractitionerPreferredLabel(requester);
      const ownerOrgRef =
        practitionerOrganizationRef(requester) ??
        practiceOrganizationRef ??
        (import.meta.env.VITE_MEDPLUM_REFERRAL_TASK_OWNER_ORG_ID?.trim()
          ? `Organization/${import.meta.env.VITE_MEDPLUM_REFERRAL_TASK_OWNER_ORG_ID.trim()}`
          : undefined);

      const draft: Task = {
        resourceType: 'Task',
        status: 'requested',
        intent: 'order',
        code: buildReferralRequestTaskCode(),
        requester: {
          reference: `Practitioner/${requesterId}`,
          ...(requesterLabel ? { display: requesterLabel } : {}),
        },
        ...referralOwnerAndMeta(ownerOrgRef),
        input: buildReferralRequestTaskInputs({
          numberOfClients: input.numberOfClients,
          acceptsInPerson: input.acceptsInPerson,
          noMinors: input.noMinors,
          noCouplesFamilies: input.noCouplesFamilies,
          onlyCashPay: input.onlyCashPay,
        }),
        authoredOn: new Date().toISOString(),
      };
      await executeTaskMutation(medplum, { action: 'create-payload', task: draft });
      await load();
    },
    [medplum, providers, practiceOrganizationRef, load],
  );

  const cancelReferralRequest = useCallback(
    async (task: Task) => {
      const m = computeReferralRequestMetrics(task);
      if (m.referralsReserved > 0) {
        const others = (task.extension ?? []).filter((e) => e.url !== CANCELLATION_QUEUED_EXT);
        await executeTaskMutation(medplum, {
          action: 'update-payload',
          task: {
            ...task,
            extension: [...others, { url: CANCELLATION_QUEUED_EXT, valueBoolean: true }],
          },
        });
      } else {
        await executeTaskMutation(medplum, { action: 'cancel', taskId: task.id });
      }
      await load();
    },
    [medplum, load],
  );

  return {
    rows,
    loading,
    error,
    scope,
    showPracticeColumn,
    providerOptions,
    providers,
    statusFilter: statusFilter as ReferralUiStatusLabel[],
    setStatusFilter: (statuses: ReferralUiStatusLabel[]) => setStatusFilter(statuses),
    refetch: load,
    createReferralRequest,
    cancelReferralRequest,
  };
}
